import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { socketService } from '../../../services/socket';
import { CustomDialog } from '../../common/components/CustomDialog';

interface Group {
    id: string;
    name: string;
    color: string;
    order: number;
}

interface GroupsEditorProps {
    externalGroups: Group[];
    externalParticipants: any[];
}

export const GroupsEditor: React.FC<GroupsEditorProps> = ({ externalGroups, externalParticipants }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [color, setColor] = useState('var(--color-accent-danger)');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [dialog, setDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'alert' | 'prompt' | 'confirm';
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'alert',
        onConfirm: () => { }
    });

    const handleSave = () => {
        if (!name) return;
        const groupData = {
            id: editingId || `G_${Date.now()}`,
            name: name.toUpperCase(),
            color
        };
        socketService.emit('save-group', groupData);
        setName('');
        setColor('var(--color-accent-danger)');
        setEditingId(null);
    };

    const handleEdit = (g: Group) => {
        setEditingId(g.id);
        setName(g.name);
        setColor(g.color);
    };

    const handleDelete = (id: string) => {
        setDialog({
            isOpen: true,
            title: t('controller.groups.deleteGroup'),
            message: t('controller.groups.deleteGroupConfirm'),
            type: 'confirm',
            onConfirm: () => {
                socketService.emit('delete-group', id);
                setDialog(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const colors = [
        '#f72d60', '#ff9933', '#ffcc00', '#00d084', '#2d6ff7', '#9b51e0', '#888888', '#ffffff'
    ];

    return (
        <div style={{ padding: '20px', color: 'var(--color-text-primary)' }}>
            <h2 style={{ color: 'var(--color-accent-danger)', marginBottom: '20px', letterSpacing: '2px' }}>{t('controller.groups.title')}</h2>

            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                {/* FORM */}
                <div style={{ width: '300px', background: 'var(--color-panel-bg)', padding: '20px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-panel-border)' }}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem', color: 'var(--color-text-muted)' }}>{editingId ? t('controller.groups.editGroup') : t('controller.groups.createNew')}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', fontWeight: 'bold' }}>{t('controller.groups.groupName')}</label>
                            <input
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder={t('controller.groups.groupNamePlaceholder')}
                                style={{ width: '100%', background: 'var(--color-input-bg)', border: '1px solid var(--color-panel-border)', color: 'var(--color-text-primary)', padding: '10px', borderRadius: 'var(--border-radius-sm)', marginTop: '5px' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', fontWeight: 'bold' }}>{t('controller.groups.themeColor')}</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' }}>
                                {colors.map(c => (
                                    <div
                                        key={c}
                                        onClick={() => setColor(c)}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '50%', background: c,
                                            cursor: 'pointer', border: color === c ? '2px solid #fff' : '2px solid transparent',
                                            boxShadow: color === c ? `0 0 10px ${c}` : 'none'
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button
                                onClick={handleSave}
                                style={{ flex: 1, background: 'var(--color-accent-danger)', color: '#fff', border: 'none', padding: '12px', borderRadius: 'var(--border-radius-sm)', fontWeight: 'bold', cursor: 'pointer' }}
                            >
                                {editingId ? t('controller.groups.update') : t('controller.groups.create')}
                            </button>
                            {editingId && (
                                <button
                                    onClick={() => { setEditingId(null); setName(''); setColor('var(--color-accent-danger)'); }}
                                    style={{ background: 'var(--color-bg-base)', color: 'var(--color-text-muted)', border: '1px solid var(--color-panel-border)', padding: '12px', borderRadius: 'var(--border-radius-sm)', cursor: 'pointer' }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* LIST */}
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
                        {externalGroups.map(g => {
                            const members = externalParticipants.filter(p => p.groupId === g.id);
                            return (
                                <div key={g.id} style={{ background: 'var(--color-panel-bg)', border: `1px solid ${g.color}33`, borderRadius: 'var(--border-radius-lg)', padding: '15px', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '5px' }}>
                                        <button onClick={() => handleEdit(g)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                                        <button onClick={() => handleDelete(g.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5 }}>🗑️</button>
                                    </div>
                                    <h4 style={{ margin: 0, color: g.color, fontSize: '1.1rem' }}>{g.name}</h4>
                                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '1.5rem', fontWeight: '900', color: g.color }}>{members.length}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', fontWeight: 'bold' }}>{t('controller.groups.members')}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {members.map(m => m.name).join(', ') || t('controller.groups.noMembers')}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '15px', height: '4px', background: g.color, borderRadius: '2px', opacity: 0.3 }} />
                                </div>
                            );
                        })}
                    </div>
                    {externalGroups.length === 0 && (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', border: '2px dashed var(--color-panel-border)', borderRadius: 'var(--border-radius-lg)' }}>
                            {t('controller.groups.noGroups')}
                        </div>
                    )}
                </div>
            </div>

            <CustomDialog
                isOpen={dialog.isOpen}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                onConfirm={dialog.onConfirm}
                onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
};
